import React, { useEffect, useState, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import Home from './pages/Home';
import G4GeDiegoIvo from './pages/G4GeDiegoIvo';
import Report from './pages/Report';
import VerifyEmail from './pages/VerifyEmail';
import MagicLink from './pages/MagicLink';
import NovaSenhaPage from './pages/NovasenhaPage';
import TesteSerieTemporal from './pages/TesteSerieTemporal';
import ShareDiegoIvoEventos from './pages/ShareDiegoIvoEventos';
import { authService } from './services/auth';
import ToastContainer from './components/UI/ToastContainer';
import LoginModal from './components/Authentication/LoginModal';
import Head from './components/SEO/Head';
import TrackingScripts from './components/SEO/TrackingScripts';
import AnnouncementBar from './components/UI/AnnouncementBar';
// Create a context for toast notifications
export const ToastContext = createContext({
  addToast: (type: 'success' | 'error', message: string) => {}
});
// Create context for login modal
export const LoginModalContext = createContext({
  openLoginModal: (isRelogin: boolean = false) => {}
});
export function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState({
    name: '',
    email: '',
    avatar: ''
  });
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRelogin, setIsRelogin] = useState(false);
  // State for toast notifications
  const [toasts, setToasts] = useState<Array<{
    id: string;
    type: 'success' | 'error';
    message: string;
  }>>([]);
  // Function to add a toast notification
  const addToast = (type: 'success' | 'error', message: string) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prevToasts => [...prevToasts, {
      id,
      type,
      message
    }]);
  };
  // Function to remove a toast notification
  const removeToast = (id: string) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  };
  // Function to open login modal
  const openLoginModal = (relogin: boolean = false) => {
    setIsRelogin(relogin);
    setIsLoginModalOpen(true);
  };
  // Function to check auth status
  const checkAuth = async () => {
    const isAuth = authService.isAuthenticated();
    const currentUser = authService.getCurrentUser();
    if (isAuth && currentUser) {
      setIsLoggedIn(true);
      setUser({
        name: currentUser.firstname && currentUser.lastname ? `${currentUser.firstname} ${currentUser.lastname}` : currentUser.email || '',
        email: currentUser.email || '',
        avatar: currentUser.avatar || ''
      });
      // Load profile data in background
      try {
        await authService.getProfile();
      } catch (error) {
        console.error('Failed to load profile data in background:', error);
        // If we get a 401, it means the token is invalid or expired
        if (error.response?.status === 401) {
          // Try to refresh the token
          const refreshed = await authService.refreshToken();
          if (!refreshed) {
            // If refresh failed, logout
            await authService.logout();
            setIsLoggedIn(false);
            setUser({
              name: '',
              email: '',
              avatar: ''
            });
          }
        }
      }
    } else {
      setIsLoggedIn(false);
      setUser({
        name: '',
        email: '',
        avatar: ''
      });
    }
    setIsAuthChecked(true);
  };
  useEffect(() => {
    // Check if user is authenticated on initial load
    checkAuth();
    // Listen for storage events to sync state across tabs
    const handleStorageChange = e => {
      // Only respond to relevant storage changes
      if (e && (e.key === 'accessToken' || e.key === 'refreshToken' || e.key === 'user' || e.key === null)) {
        checkAuth();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom auth events
    window.addEventListener('auth-change', checkAuth);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-change', checkAuth);
    };
  }, []);
  const login = userData => {
    setUser(userData);
    setIsLoggedIn(true);
    // The tokens are already stored by the auth service
    // Removed toast notification here to prevent duplicate toasts
  };
  const logout = async () => {
    await authService.logout();
    setUser({
      name: '',
      email: '',
      avatar: ''
    });
    setIsLoggedIn(false);
  };
  // Don't render until we've checked authentication
  if (!isAuthChecked) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-0" data-mp="3glef66abpbsw">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1649ff]" data-id="element-1" data-mp="1w4oqy4f216pl"></div>
      </div>;
  }
  return <ToastContext.Provider value={{
    addToast
  }} data-id="element-2" data-mp="206tnbaww0hfx">
      <LoginModalContext.Provider value={{
      openLoginModal
    }} data-id="element-3" data-mp="254v2vlv3tymn">
        <Router data-id="element-4" data-mp="29w3dgl867q1e">
          <Head data-id="element-5" data-mp="29wv6ppeapboi" />
          <TrackingScripts data-id="element-6" data-mp="29xejyst03bw7" />
          <div className="flex flex-col min-h-screen bg-white w-full overflow-x-hidden" data-id="element-7" data-mp="29xo5a333ouvk">
            <div className="flex flex-col min-h-screen" data-id="element-8" data-mp="2ex0xl3cqqu5j">
              <AnnouncementBar data-id="element-9" data-mp="2jsy7orxev49v" />
              <Header isLoggedIn={isLoggedIn} user={user} onLogout={logout} data-id="element-10" data-mp="2jw41gkgziubx" />
              <main className="flex-grow w-full" data-id="element-11" data-mp="2nw4ec0ofqxgv">
                <Routes data-id="element-12" data-mp="2sufoydmk1c05">
                  <Route path="/" element={<Home isLoggedIn={isLoggedIn} onLogin={login} user={user} data-id="element-14" data-mp="ti3jdqw0aum2" />} data-id="element-13" data-mp="2stmplxpxdos1" />
                  <Route path="/g4-ge-diego-ivo" element={<G4GeDiegoIvo isLoggedIn={isLoggedIn} onLogin={login} user={user} data-id="element-16" data-mp="1wtixj5izm8yp" />} data-id="element-15" data-mp="21slqhrlp0oqb" />
                  <Route path="/relatorio/:uuid" element={<Report data-id="element-18" data-mp="2kq6ih7jr83f5" />} data-id="element-17" data-mp="25rukmxknaome" />
                  <Route path="/verificar-email" element={<VerifyEmail data-id="element-20" data-mp="2kobu1j99xt6b" />} data-id="element-19" data-mp="2kpemvwq5q5ov" />
                  <Route path="/magic-link" element={<MagicLink data-id="element-22" data-mp="2fqk58y3xen6c" />} data-id="element-21" data-mp="2frv7gthq3mvc" />
                  <Route path="/nova-senha" element={<NovaSenhaPage data-id="element-24" data-mp="2tj9urcinya87" />} data-id="element-23" data-mp="2fpirumzftolg" />
                  <Route path="/teste-serie-temporal" element={<TesteSerieTemporal data-id="element-26" data-mp="2pig44bdu6hbm" />} data-id="element-25" data-mp="2ti8coo38cngz" />
                  <Route path="/traktion" element={<ShareDiegoIvoEventos isLoggedIn={isLoggedIn} onLogin={login} user={user} data-id="element-28" data-mp="2yi35wyah9p57" />} data-id="element-27" data-mp="33go5qwlti6t4" />
                </Routes>
              </main>
              <Footer data-id="element-29" data-mp="3n0n0iuq53pv9" />
            </div>
            <ToastContainer toasts={toasts} onClose={removeToast} data-id="element-30" data-mp="3868gqryr4cfj" />
            <LoginModal isOpen={isLoginModalOpen} onClose={() => {
            setIsLoginModalOpen(false);
            setIsRelogin(false);
          }} onRegisterClick={() => {
            setIsLoginModalOpen(false);
            // Handle register modal opening if needed
          }} isRelogin={isRelogin} data-id="element-31" data-mp="387teeqgd4bhm" />
          </div>
        </Router>
      </LoginModalContext.Provider>
    </ToastContext.Provider>;
}